import React, { useState, useRef, useEffect } from 'react';
import { cn } from '../../lib/utils';
import { useTheme } from '../../contexts/ThemeContext';
import { ChevronDownIcon } from './Icons';
import TextInput from './TextInput';
import { useFlexibleList } from '../../hooks/useSWR';

const Autocomplete = ({ 
  endpoint,
  options: staticOptions = [], 
  value = '', 
  onChange, 
  placeholder = "Type to search...", 
  className,
  disabled = false,
  required = false,
  error = null,
  maxLength = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const { theme } = useTheme();

  // Build params for the API call - only send search if user has typed something
  // When no search term, API returns all options (or first N)
  const searchParams = React.useMemo(() => {
    return searchTerm && searchTerm.trim() ? { search: searchTerm.trim() } : {};
  }, [searchTerm]);
  
  // Fetch options from API if endpoint is provided
  // useFlexibleList expects (endpoint, params) as separate arguments
  const { data: apiOptions = [], isLoading, isError } = useFlexibleList(endpoint, searchParams);

  // Debug logging (remove in production)
  useEffect(() => {
    if (endpoint) {
      console.log('Autocomplete Debug:', {
        endpoint,
        searchParams,
        apiOptions,
        apiOptionsType: Array.isArray(apiOptions),
        apiOptionsLength: Array.isArray(apiOptions) ? apiOptions.length : 'not array',
        isLoading,
        isError
      });
    }
  }, [endpoint, apiOptions, searchParams, isLoading, isError]);

  // Use API options directly (API handles filtering) or static options
  const filteredOptions = React.useMemo(() => {
    if (endpoint) {
      // API returns filtered results, use them directly
      // The API returns an array of strings, so we use them as-is
      const options = Array.isArray(apiOptions) ? apiOptions.filter(Boolean) : [];
      return options;
    } else {
      // For static options, do client-side filtering
      if (!inputValue) return staticOptions.slice(0, 20);
      
      const lowerInput = inputValue.toLowerCase();
      return staticOptions
        .filter(opt => {
          const optionText = typeof opt === 'string' ? opt : (opt.title || opt.name || String(opt));
          return optionText.toLowerCase().includes(lowerInput);
        })
        .slice(0, 20);
    }
  }, [inputValue, endpoint, apiOptions, staticOptions]);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setSearchTerm(newValue);
    setIsOpen(true);
    
    // Call onChange immediately for free text input
    if (onChange) {
      onChange(newValue);
    }
  };

  const handleSelectOption = (option) => {
    const optionText = typeof option === 'string' ? option : (option.title || option.name || String(option));
    setInputValue(optionText);
    setSearchTerm('');
    setIsOpen(false);
    if (onChange) {
      onChange(optionText);
    }
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    } else if (e.key === 'ArrowDown' && filteredOptions.length > 0) {
      e.preventDefault();
      // Focus first option (could be enhanced to navigate through options)
    }
  };

  return (
    <div ref={dropdownRef} className={cn("relative", className)}>
      <div className="relative">
        <TextInput
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          error={error}
          maxLength={maxLength}
          className="pr-8"
        />
        <ChevronDownIcon 
          className={cn(
            "absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 pointer-events-none transition-transform",
            theme.text.muted,
            isOpen && "rotate-180"
          )} 
        />
      </div>

      {isOpen && (
        <div className={cn(
          "absolute z-50 w-full mt-1 border rounded-md shadow-lg max-h-60 overflow-y-auto",
          theme.colors.surface,
          theme.border.light
        )}>
          {isLoading && (
            <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
              Loading...
            </div>
          )}
          {!isLoading && isError && (
            <div className="px-3 py-2 text-sm text-red-500 dark:text-red-400">
              Error loading options
            </div>
          )}
          {!isLoading && !isError && filteredOptions.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
              No options found
            </div>
          )}
          {!isLoading && filteredOptions.length > 0 && filteredOptions.map((option, index) => {
            const optionText = typeof option === 'string' ? option : (option.title || option.name || String(option));
            const isSelected = inputValue === optionText;
            
            return (
              <div
                key={index}
                onClick={() => handleSelectOption(option)}
                className={cn(
                  "px-3 py-2 cursor-pointer transition-colors",
                  isSelected 
                    ? "bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100" 
                    : "hover:bg-gray-100 dark:hover:bg-gray-700",
                  theme.text.primary
                )}
              >
                {optionText}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Autocomplete;

